import { useEffect, useRef, useState } from "react";

// =============================================================================
// GPS capture hook
// =============================================================================
// Wraps navigator.geolocation with a watchPosition + timeout pattern suited
// to outdoor construction: get a best-effort first fix fast, then keep
// refining until we have good accuracy or the user hits submit. Works on
// both iOS and Android PWAs in standalone mode.
// =============================================================================

export interface GpsFix {
  latitude: number;
  longitude: number;
  accuracy_m: number;
  captured_at: string;     // ISO
  source: "device" | "manual";
}

export interface GpsState {
  status: "idle" | "requesting" | "fix" | "denied" | "unavailable" | "timeout";
  fix: GpsFix | null;
  error: string | null;
  /** Trigger a fresh capture (useful when the user moves to a new spot). */
  refresh: () => void;
  /** Manually set a location (e.g. pinned from project_site centroid). */
  override: (lat: number, lng: number) => void;
}

const FAST_TIMEOUT_MS  = 6_000;   // first fix
const REFINE_TIMEOUT_MS = 20_000; // keep watching for a better fix

export function useGps(autoStart = true): GpsState {
  const [status, setStatus] = useState<GpsState["status"]>("idle");
  const [fix, setFix] = useState<GpsFix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  function clearWatchers() {
    if (watchIdRef.current != null && "geolocation" in navigator) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function start() {
    clearWatchers();
    setError(null);

    if (!("geolocation" in navigator)) {
      setStatus("unavailable");
      setError("Your browser doesn't support geolocation");
      return;
    }

    setStatus("requesting");
    let gotFirst = false;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        gotFirst = true;
        const next: GpsFix = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          captured_at: new Date().toISOString(),
          source: "device",
        };
        // Keep the most accurate fix seen so far
        setFix((prev) => (!prev || next.accuracy_m < prev.accuracy_m ? next : prev));
        setStatus("fix");
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setStatus("denied");
          setError("Location permission denied");
        } else if (err.code === err.TIMEOUT && !gotFirst) {
          setStatus("timeout");
          setError("Couldn't get a location fix");
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setStatus("unavailable");
          setError("Location unavailable (no GPS signal)");
        }
      },
      { enableHighAccuracy: true, timeout: FAST_TIMEOUT_MS, maximumAge: 30_000 },
    );

    // Stop watching after the refine window — we have our best fix
    timerRef.current = window.setTimeout(clearWatchers, REFINE_TIMEOUT_MS);
  }

  useEffect(() => {
    if (autoStart) start();
    return clearWatchers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    fix,
    error,
    refresh: start,
    override: (latitude, longitude) => {
      setFix({
        latitude, longitude, accuracy_m: 0,
        captured_at: new Date().toISOString(),
        source: "manual",
      });
      setStatus("fix");
      clearWatchers();
    },
  };
}

/** Format a GPS fix for the watermark corner stamp. */
export function formatGps(fix: GpsFix | null): string {
  if (!fix) return "GPS: unknown";
  const dir = (v: number, pos: string, neg: string) => (v >= 0 ? pos : neg);
  const lat = `${Math.abs(fix.latitude).toFixed(5)}°${dir(fix.latitude, "N", "S")}`;
  const lng = `${Math.abs(fix.longitude).toFixed(5)}°${dir(fix.longitude, "E", "W")}`;
  return `${lat} ${lng} ±${Math.round(fix.accuracy_m)}m`;
}
